import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faChevronLeft,
    faChevronDown,
    faLock,
    faPencil,
    faCopy,
    faTrashCan,
    faSpinner,
    faFileExcel,
    faFileCsv,
    faTableList,
    faClockRotateLeft,
    faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import { useFlashMessage } from '../../components/FlashMessage';
import { extractApiError } from '../../utils/errorMessages';
import { fetchReportDetail, runReport, downloadReport, deleteReport } from '../../services/reportingService';
import ResultTable from './ResultTable';
import PastDownloadsDrawer from './PastDownloadsDrawer';

/**
 * One compact "pick any of these" control. The choices are the values the LAST
 * run actually returned, so a person can only filter to something that is
 * really in the report — never to an empty screen.
 */
const QuickFilterSelect = ({ label, options, selected, onChange }) => {
    const [open, setOpen] = useState(false);
    const holder = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        const onDocumentMouseDown = (e) => {
            if (holder.current && !holder.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', onDocumentMouseDown);
        return () => document.removeEventListener('mousedown', onDocumentMouseDown);
    }, [open]);

    const toggle = (value) => {
        onChange(selected.includes(value)
            ? selected.filter((v) => v !== value)
            : [...selected, value]);
    };

    const summary = selected.length === 0
        ? 'All'
        : selected.length === 1 ? selected[0] : `${selected.length} chosen`;

    return (
        <div className="rc-field rc-multi" ref={holder}>
            <span className="rc-field-label">{label}</span>
            <button
                type="button"
                className="rc-multi-button"
                onClick={() => setOpen((o) => !o)}
                aria-expanded={open}
                aria-haspopup="true"
            >
                <span className="rc-multi-summary">{summary}</span>
                <FontAwesomeIcon icon={faChevronDown} className="rc-multi-caret" />
            </button>
            {open && (
                <div className="rc-multi-panel" role="group" aria-label={label}>
                    {options.length === 0 ? (
                        <div className="rc-multi-empty">
                            Show the report once and the choices appear here.
                        </div>
                    ) : (
                        <>
                            {selected.length > 0 && (
                                <button type="button" className="rc-multi-clear" onClick={() => onChange([])}>
                                    Clear
                                </button>
                            )}
                            {options.map((option) => (
                                <label key={option} className="rc-multi-option">
                                    <input
                                        type="checkbox"
                                        checked={selected.includes(option)}
                                        onChange={() => toggle(option)}
                                    />
                                    <span>{option}</span>
                                </label>
                            ))}
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

/**
 * One report on screen: choose the period and the filters, press Show, read the
 * table, take the file. Nothing here decides what a row may contain — the
 * server runs the report against what this person is allowed to see and the
 * screen shows exactly what came back, notes and all.
 */
const ReportViewer = ({ report, breadcrumbRoot, onBack, canBuild = false, onEdit, onCopy, onDeleted }) => {
    const { showError, showSuccess } = useFlashMessage();
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [period, setPeriod] = useState({ key: null, from: '', to: '', include_undated: false });
    const [quickFilters, setQuickFilters] = useState({});
    const [result, setResult] = useState(null);
    const [running, setRunning] = useState(false);
    const [runError, setRunError] = useState('');
    const [downloading, setDownloading] = useState('');
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);
    // Bumped after a download so the drawer re-reads and shows the new file.
    const [runsKey, setRunsKey] = useState(0);

    const execute = useCallback(async (periodArg, filtersArg) => {
        setRunning(true);
        setRunError('');
        try {
            const data = await runReport(report.id, { period: periodArg, quick_filters: filtersArg });
            setResult(data);
        } catch (err) {
            setResult(null);
            setRunError(extractApiError(err, 'Could not run this report.'));
        } finally {
            setRunning(false);
        }
    }, [report.id]);

    // Opening the report loads its shape, takes the period the server suggests,
    // and runs it once so there is something to read straight away.
    const openReport = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const data = await fetchReportDetail(report.id);
            setDetail(data);
            const suggested = data.default_period || {};
            const startPeriod = {
                key: suggested.key || null,
                from: suggested.from || '',
                to: suggested.to || '',
                include_undated: Boolean(suggested.include_undated),
            };
            setPeriod(startPeriod);
            setQuickFilters({});
            await execute(startPeriod, {});
        } catch (err) {
            setError(extractApiError(err, 'Could not open this report.'));
        } finally {
            setLoading(false);
        }
    }, [report.id, execute]);

    useEffect(() => {
        openReport();
    }, [openReport]);

    const handleDownload = async (format) => {
        setDownloading(format);
        try {
            const fileName = await downloadReport(
                report.id, { period, quick_filters: quickFilters }, format,
            );
            showSuccess(`Saved ${fileName}.`);
            setRunsKey((key) => key + 1);
        } catch (err) {
            showError(extractApiError(err, 'Could not download this report.'));
        } finally {
            setDownloading('');
        }
    };

    const name = detail?.name || report.name;
    const isLocked = detail?.is_locked ?? report.is_locked;
    const busy = running || Boolean(downloading);
    // The server already decided both of these on the card; they are read, not
    // worked out again here.
    const canEdit = Boolean(detail?.can_edit ?? report.can_edit) && !isLocked;
    const canCopy = canBuild && (report.kind === 'standard' || report.kind === 'shared');
    // Wider than Edit on purpose: someone without the builder can keep a report
    // Ask AI made for them, so they must be able to throw their own away again.
    const canDelete = Boolean(detail?.can_delete ?? report.can_delete);

    const handleDelete = async () => {
        if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
        setDeleting(true);
        try {
            await deleteReport(report.id);
            showSuccess(`"${name}" was deleted.`);
            if (onDeleted) onDeleted(report);
            onBack();
        } catch (err) {
            showError(extractApiError(err, 'Could not delete this report.'));
            setDeleting(false);
        }
    };

    return (
        <div className="rc-viewer">
            <div className="rc-breadcrumb">
                <button type="button" className="rc-crumb-btn" onClick={onBack}>
                    <FontAwesomeIcon icon={faChevronLeft} /> {breadcrumbRoot}
                </button>
                <span className="rc-crumb-sep">›</span>
                <span className="rc-crumb-current">{name}</span>
            </div>

            <div className="rc-viewer-head">
                <h2 className="rc-viewer-title">{name}</h2>
                {isLocked && (
                    <span className="rc-pill rc-pill-locked">
                        <FontAwesomeIcon icon={faLock} /> Locked format. Columns cannot be changed.
                    </span>
                )}
                {(canEdit || canCopy || canDelete) && (
                    <div className="rc-viewer-head-actions">
                        {canEdit && (
                            <button type="button" className="rc-btn-ghost" onClick={() => onEdit(report)}>
                                <FontAwesomeIcon icon={faPencil} /> Edit
                            </button>
                        )}
                        {canCopy && (
                            <button type="button" className="rc-btn-ghost" onClick={() => onCopy(report)}>
                                <FontAwesomeIcon icon={faCopy} /> Copy to My reports
                            </button>
                        )}
                        {canDelete && (
                            <button type="button" className="rc-btn-ghost" onClick={handleDelete} disabled={deleting}>
                                <FontAwesomeIcon icon={faTrashCan} /> {deleting ? 'Deleting…' : 'Delete'}
                            </button>
                        )}
                    </div>
                )}
            </div>

            {loading ? (
                <div className="rc-panel rc-panel-message">Loading this report…</div>
            ) : error ? (
                <div className="rc-panel">
                    <div className="rc-error">
                        <FontAwesomeIcon icon={faTriangleExclamation} /> {error}
                    </div>
                </div>
            ) : (
                <>
                    <div className="rc-panel">
                        <div className="rc-filter-bar">
                            <div className="rc-field">
                                <label className="rc-field-label" htmlFor="rc-period-from">From</label>
                                <input
                                    id="rc-period-from"
                                    type="date"
                                    className="rc-input"
                                    value={period.from || ''}
                                    onChange={(e) => setPeriod((p) => ({ ...p, from: e.target.value }))}
                                />
                            </div>
                            <div className="rc-field">
                                <label className="rc-field-label" htmlFor="rc-period-to">To</label>
                                <input
                                    id="rc-period-to"
                                    type="date"
                                    className="rc-input"
                                    value={period.to || ''}
                                    onChange={(e) => setPeriod((p) => ({ ...p, to: e.target.value }))}
                                />
                            </div>
                            {(detail?.period_options || []).length > 0 && (
                                <div className="rc-field">
                                    <label className="rc-field-label" htmlFor="rc-period-key">Applies to</label>
                                    <select
                                        id="rc-period-key"
                                        className="rc-select"
                                        value={period.key || ''}
                                        onChange={(e) => setPeriod((p) => ({ ...p, key: e.target.value }))}
                                    >
                                        {detail.period_options.map((option) => (
                                            <option key={option.key} value={option.key}>{option.label}</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {(detail?.quick_filters || []).map((filter) => (
                                <QuickFilterSelect
                                    key={filter.key}
                                    label={filter.label}
                                    options={result?.filter_options?.[filter.key] || []}
                                    selected={quickFilters[filter.key] || []}
                                    onChange={(values) => setQuickFilters((current) => ({
                                        ...current, [filter.key]: values,
                                    }))}
                                />
                            ))}

                            {/* A row with no date is left out of every period. This
                                is the only way to ask for those rows back. */}
                            <label className="rc-check">
                                <input
                                    type="checkbox"
                                    checked={Boolean(period.include_undated)}
                                    onChange={(e) => setPeriod((p) => ({
                                        ...p, include_undated: e.target.checked,
                                    }))}
                                />
                                <span>Include rows with no date</span>
                            </label>

                            <div className="rc-filter-actions">
                                <button
                                    type="button"
                                    className="rc-btn-primary"
                                    onClick={() => execute(period, quickFilters)}
                                    disabled={busy}
                                >
                                    <FontAwesomeIcon icon={running ? faSpinner : faTableList} spin={running} />
                                    {running ? 'Showing…' : 'Show'}
                                </button>
                                <button
                                    type="button"
                                    className="rc-btn-ghost"
                                    onClick={() => handleDownload('xlsx')}
                                    disabled={busy}
                                >
                                    <FontAwesomeIcon
                                        icon={downloading === 'xlsx' ? faSpinner : faFileExcel}
                                        spin={downloading === 'xlsx'}
                                    />
                                    {downloading === 'xlsx' ? 'Preparing…' : 'Download Excel'}
                                </button>
                                <button
                                    type="button"
                                    className="rc-btn-ghost"
                                    onClick={() => handleDownload('csv')}
                                    disabled={busy}
                                >
                                    <FontAwesomeIcon
                                        icon={downloading === 'csv' ? faSpinner : faFileCsv}
                                        spin={downloading === 'csv'}
                                    />
                                    {downloading === 'csv' ? 'Preparing…' : 'CSV'}
                                </button>
                                <button
                                    type="button"
                                    className="rc-link-btn"
                                    onClick={() => setDrawerOpen(true)}
                                >
                                    <FontAwesomeIcon icon={faClockRotateLeft} /> Past downloads
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="rc-panel">
                        <ResultTable
                            result={result}
                            running={running}
                            error={runError}
                            caption={`${name}${result?.period?.label ? ` for ${result.period.label}` : ''}`}
                            idleText="Press Show to run this report."
                        />
                    </div>
                </>
            )}

            {drawerOpen && (
                <PastDownloadsDrawer
                    reportId={report.id}
                    reportName={name}
                    refreshKey={runsKey}
                    onClose={() => setDrawerOpen(false)}
                />
            )}
        </div>
    );
};

export default ReportViewer;
